import { isMoviegoerAppMode } from 'src/config'
import KdsClient from 'src/models/kds/KdsClient'
import Model, { JsonObject } from 'src/models/model'
import RegisterClient from 'src/models/register/RegisterClient'
import User from 'src/models/user/User'

export default class ClientContext extends Model {
  static name = 'ClientContext'

  static noId = true
  static isContext = true

  static define () {
    this.fields = {
      currentUser: {
        type: User,
        output: true
      },
      currentRegisterClient: {
        type: RegisterClient,
        output: true
      },
      currentKdsClient: {
        type: KdsClient,
        output: true
      },
      currentHardware: {
        type: RegisterClient,
        output: true
      },
      currentLocale: {
        type: String,
        output: true
      },
      clientReloadProgress: {
        type: Number,
        default: 0,
        local: true
      },
      permittedFrontendActions: {
        type: [String],
        default: () => ([]),
        output: true
      },
      // Should not be accessible directly
      _previewedPermittedFrontendActions: {
        type: [String],
        default: () => ([]),
        local: true
      },
      featureFlags: {
        type: JsonObject,
        default: () => ({}),
        output: true
      }
    }
  }

  isFeatureEnabled (featureName) {
    return this.featureFlags[featureName] === true
  }

  /**
   * Return true if ALL actions are permitted, false otherwise.
   * @param  {string | string[]} actions DefaultPermissionAction or custom action values.
   * @returns {boolean}
   */
  isPermitted (...actions) {
    if (isMoviegoerAppMode && !this.currentUser?.employee && !this.currentUser?.admin) {
      // Permissions are only expected to be assigned to employees/admin, but just in case some consumer app
      // checks for non-employee permissions, we'll always allow consumer users to avoid blocking functionality.
      // TODO: This skip should be removed once we've verified it won't negatively affect moviegoer experience.
      return true
    }

    actions = _.flattenDeep(actions)
    if (_.isEmpty(actions)) return false

    let effectivePermittedActions = this.permittedFrontendActions
    if (!_.isEmpty(this.previewedPermittedFrontendActions)) {
      effectivePermittedActions = this.previewedPermittedFrontendActions
    }

    return _.every(actions, action => _.includes(effectivePermittedActions, action))
  }

  /**
   * Return true if ANY action is permitted, false otherwise.
   * @param  {string | string[]} actions DefaultPermissionAction or custom action values.
   * @returns {boolean}
   */
  isAnyPermitted (...actions) {
    if (isMoviegoerAppMode && !this.currentUser?.employee && !this.currentUser?.admin) {
      // Permissions are only expected to be assigned to employees/admin, but just in case some consumer app
      // checks for non-employee permissions, we'll always allow consumer users to avoid blocking functionality.
      // TODO: This skip should be removed once we've verified it won't negatively affect moviegoer experience.
      return true
    }

    actions = _.flattenDeep(actions)
    if (_.isEmpty(actions)) return false

    let effectivePermittedActions = this.permittedFrontendActions
    if (!_.isEmpty(this.previewedPermittedFrontendActions)) {
      effectivePermittedActions = this.previewedPermittedFrontendActions
    }

    return _.some(actions, action => _.includes(effectivePermittedActions, action))
  }

  get isPreviewPermissionsPermitted () {
    return _.includes(this.permittedFrontendActions, this.permissions.Security.View)
  }

  get isPreviewingPermissions () {
    return !_.isEmpty(this.previewedPermittedFrontendActions)
  }

  // Provide indirect access only so we can check to see if we should actually return the previewed actions
  get previewedPermittedFrontendActions () {
    if (this.isPreviewPermissionsPermitted) {
      return _.clone(this._previewedPermittedFrontendActions)
    } else {
      if (!_.isEmpty(this._previewedPermittedFrontendActions)) {
        this.stopPreviewingPermissions()
      }
      return []
    }
  }

  set previewedPermittedFrontendActions (_actions) {
    throw new Error('previewedPermittedFrontendActions is read-only')
  }

  /** @param {string[]} permissionActions */
  previewPermissions (permissionActions) {
    if (this.isPreviewPermissionsPermitted && _.isArray(permissionActions)) {
      this._previewedPermittedFrontendActions = _.clone(permissionActions)
    }
  }

  stopPreviewingPermissions () {
    if (!_.isEmpty(this._previewedPermittedFrontendActions)) {
      this._previewedPermittedFrontendActions = []
    }
    const localStorageKey = Model.app.config.globalProperties.$constants.localStorageKeys.previewedPermissions
    try {
      localStorage.removeItem(localStorageKey)
    } catch (e) {
      console.error('Error removing previewed permissions from localStorage', e)
    }
  }

  async changeCircuitOrSite () {
    /**
     * I've moved this into the client context because the user object gets
     * destroyed and replaced when the client context is refreshed. In Vue 3,
     * reactivity breaks once the user object is destroyed. So the destroyed
     * user object is updating itself with new data, but its subscribers aren't
     * receiving the new data.
     */
    if (!this.currentUser) {
      return {
        circuitIdChanged: false,
        siteIdChanged: false
      }
    }

    let circuitIdChanged = this.currentUser.circuitId !== this.currentUser.originalData.circuitId
    let siteIdChanged = this.currentUser.siteId !== this.currentUser.originalData.siteId

    const updatedUserData = {
      id: this.currentUser.id
    }

    if (circuitIdChanged) {
      if (_.isNil(this.currentUser.circuitId)) {
        circuitIdChanged = false
        siteIdChanged = false
        return { circuitIdChanged, siteIdChanged }
      }
      updatedUserData.circuitId = this.currentUser.circuitId
    } else if (siteIdChanged) {
      if (_.isNil(this.currentUser.siteId)) {
        circuitIdChanged = false
        siteIdChanged = false
        return { circuitIdChanged, siteIdChanged }
      }
      updatedUserData.siteId = this.currentUser.siteId
    } else {
      circuitIdChanged = false
      siteIdChanged = false
      return { circuitIdChanged, siteIdChanged }
    }

    const user = new User(updatedUserData, { fields: ['id', circuitIdChanged ? 'circuitId' : 'siteId'] })
    await user.save()
    user.destroy()

    await this.currentUser.fetch(true)

    return { circuitIdChanged, siteIdChanged }
  }
}
